import { ContactPoint } from '@/__generated__/graphql';
import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { env } from 'next-runtime-env';

const createApolloClient = (): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    uri: env('GRAPHQL_ENDPOINT') || '',
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            goals: {
              merge(existing, incoming) {
                // Keep both goal and goals queries separate in the cache
                return { ...existing, ...incoming };
              },
            },
            post: {
              keyArgs: false,
              merge(existing, incoming, { mergeObjects }) {
                // to merge the existing and incoming objects
                return mergeObjects(existing, incoming);
              },
            },
            organization: {
              merge: true,
            },
            organizationGrant: {
              merge(existing, incoming, { mergeObjects }) {
                // to merge the existing and incoming objects
                return mergeObjects(existing, incoming);
              },
            },
          },
        },
        GoalQueries: {
          fields: {
            goals: {
              // Use organizationId and first as key args to separate different queries
              keyArgs: ['organizationId', 'first'],
              merge(existing, incoming, { args }) {
                if (!args) return incoming;
                // If it's a single goal query, don't merge with the list
                if (args.id) return incoming;

                // For the goals list, merge properly
                if (!existing) return incoming;
                return {
                  ...existing,
                  ...incoming,
                  edges: [...(existing.edges || []), ...(incoming.edges || [])],
                };
              },
            },
            goal: {
              // Single goal queries are stored separately by ID
              keyArgs: ['id'],
            },
          },
        },
        Goal: {
          fields: {
            metrics: {
              merge(existing = [], incoming = []) {
                return incoming;
              },
            },
          },
        },
        OrganizationGrant: {
          keyFields: ['id'],
        },
        Organization: {
          fields: {
            contactPoints: {
              merge(
                existing: ContactPoint[],
                incoming: ContactPoint[],
                { mergeObjects, readField }
              ) {
                const merged = existing ? existing.slice(0) : [];
                const emailToIndex: Record<string, number> =
                  Object.create(null);
                if (existing) {
                  existing.forEach((cp, index) => {
                    cp.communicationChannels.forEach((channel) => {
                      if (channel.type === 'EMAIL') {
                        if (channel.addressee) {
                          const email = readField<string>('addressee', channel);
                          if (email !== undefined) {
                            emailToIndex[email] = index;
                          }
                        }
                      }
                    });
                  });
                }
                incoming.forEach((cp) => {
                  cp.communicationChannels.forEach((channel) => {
                    const email = readField<string>('addressee', channel);
                    if (email === undefined) return;
                    const index = emailToIndex[email];
                    if (typeof index === 'number') {
                      merged[index] = mergeObjects(merged[index], cp);
                    } else {
                      emailToIndex[email] = merged.length;
                      merged.push(cp);
                    }
                  });
                });
                return merged;
              },
            },
          },
        },
      },
    }),
    connectToDevTools: true,
  });
};

export default createApolloClient;
